import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from "react-router-dom";
import { GoArrowUpRight } from 'react-icons/go';
import { Layout, Drawer, Button } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { Sun, Moon, X } from 'lucide-react';
import { motion } from 'framer-motion';

const { Header: AntHeader } = Layout;

const Header = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Theme state
    const [isDarkMode, setIsDarkMode] = useState(() => {
        if (typeof window !== 'undefined') {
            const saved = localStorage.getItem('theme');
            if (saved) return saved === 'dark';
            return true; // Default to dark mode
        }
        return true;
    });

    useEffect(() => {
        const root = document.documentElement;
        if (isDarkMode) {
            root.classList.add('dark');
            localStorage.setItem('theme', 'dark');
        } else {
            root.classList.remove('dark');
            localStorage.setItem('theme', 'light');
        }
    }, [isDarkMode]);

    useEffect(() => {
        // Init theme from localstorage if present
        const savedTheme = localStorage.getItem('theme');
        if (savedTheme) {
            setIsDarkMode(savedTheme === 'dark');
        }
    }, []);

    const toggleTheme = () => setIsDarkMode(prev => !prev);

    const navItems = [
        { key: "/", label: "Home" },
        { key: "/products", label: "Products" },
        { key: "/case-studies", label: "Case Studies" },
        { key: "/about", label: "About" },
        { key: "/services", label: "Services" },
    ];

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setIsMenuOpen(false);
    }, [location]);

    const handleNavClick = (href) => {
        setIsMenuOpen(false);
        if (href.startsWith('/#')) {
            const id = href.substring(2);
            const element = document.getElementById(id);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        } else {
            navigate(href);
        }
    };

    const handleContactClick = () => {
        setIsMenuOpen(false);
        navigate('/contact');
    };

    return (
        <AntHeader
            className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-500 ${scrolled
                ? 'bg-white/95 dark:bg-slate-950/95 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.12)]'
                : 'bg-transparent border-b border-transparent'
                }`}
            style={{ padding: 0, height: 'auto', lineHeight: 'normal' }}
        >
            <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-2.5 sm:py-3 md:py-4">
                <div className="relative flex items-center justify-between">

                    {/* Logo */}
                    <div className="flex-shrink-0 flex items-center min-w-0">
                        <Link to="/" className="flex items-center gap-2 sm:gap-2.5 group">
                            <div className="relative flex-shrink-0">
                                <div className="absolute -inset-2 bg-primary/20 rounded-full blur opacity-0 group-hover:opacity-100 transition duration-500"></div>
                                <img
                                    src="https://utfs.io/f/wzoc0SJJ8iMqmv8tcTUCV0hUWxjMFoDS38r1AKmZ5kftGwsN"
                                    alt="Connected Bytes Software Service Logo"
                                    className="relative h-7 md:h-8 w-auto z-10"
                                />
                            </div>
                            <div className="flex flex-col text-left leading-tight font-heading">
                                <span className="text-xs sm:text-sm md:text-base font-extrabold text-foreground tracking-tight whitespace-nowrap">Connected Bytes</span>
                                <span className="text-[9px] sm:text-[10px] md:text-xs font-bold text-primary tracking-wider uppercase whitespace-nowrap">Software Service</span>
                            </div>
                        </Link>
                    </div>

                    {/* Desktop Menu - Absolutely centered relative to full navbar width */}
                    <nav className="hidden md:flex items-center space-x-1 lg:space-x-4 absolute left-1/2 -translate-x-1/2">
                        {navItems.map((item) => {
                            const isActive = item.key === "/"
                                ? location.pathname === "/"
                                : location.pathname.startsWith(item.key);

                            return (
                                <button
                                    key={item.key}
                                    onClick={() => handleNavClick(item.key)}
                                    className={`relative px-2 lg:px-4 py-2 text-[13px] lg:text-[15px] font-bold tracking-tight transition-all duration-300 whitespace-nowrap ${isActive
                                        ? 'text-primary'
                                        : 'text-foreground font-semibold hover:text-primary dark:text-white/90 dark:hover:text-primary'
                                        }`}
                                >
                                    <span className="relative z-10">{item.label}</span>
                                    {isActive && (
                                        <motion.div
                                            layoutId="activeTab"
                                            className="absolute bottom-0 left-2 right-2 h-0.5 bg-primary rounded-full shadow-[0_0_10px_rgba(var(--color-primary),0.5)]"
                                            initial={false}
                                            transition={{ type: "spring", stiffness: 500, damping: 30 }}
                                        />
                                    )}
                                    {/* Subtle hover background block */}
                                    <div className="absolute inset-0 rounded-xl bg-primary/5 opacity-0 hover:opacity-100 transition-opacity -z-10"></div>
                                </button>
                            );
                        })}
                    </nav>

                    {/* Right Side Tools */}
                    <div className="flex-shrink-0 flex items-center gap-2 sm:gap-3">
                        {/* Theme Toggle */}
                        <button
                            onClick={toggleTheme}
                            className="p-2 rounded-full border border-border bg-card/50 hover:bg-card text-foreground transition-colors flex items-center justify-center"
                            aria-label="Toggle Theme"
                        >
                            {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                        </button>

                        {/* Contact Button Desktop - Visible from 'lg' to prevent crowding */}
                        <div className="hidden lg:inline-flex flex-shrink-0">
                            <motion.button
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                onClick={handleContactClick}
                                className="inline-flex items-center gap-1 px-5 py-2 rounded-full bg-primary text-primary-foreground font-bold text-sm shadow-md hover:shadow-lg transition-all whitespace-nowrap"
                            >
                                Contact Us <GoArrowUpRight className="text-lg" />
                            </motion.button>
                        </div>

                        {/* Mobile Hamburger Menu - Visible below 'md' */}
                        <div className="md:hidden flex items-center">
                            <button
                                onClick={() => setIsMenuOpen(true)}
                                className="p-2 rounded-lg border border-border/50 bg-card/50 hover:bg-card text-foreground focus:outline-none flex items-center justify-center transition-colors"
                                aria-label="Open menu"
                            >
                                <MenuOutlined className="text-lg" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Drawer - Custom close button for better visibility */}
            <Drawer
                title={null}
                placement="right"
                onClose={() => setIsMenuOpen(false)}
                open={isMenuOpen}
                width="min(280px, 85vw)"
                className={isDarkMode ? 'dark' : ''}
                rootClassName={isDarkMode ? 'dark' : ''}
                closeIcon={null}
                styles={{
                    body: { padding: 0, backgroundColor: isDarkMode ? '#030712' : '#ffffff' },
                    header: { display: 'none' }
                }}
            >
                {/* Custom Close Button */}
                <div className="flex justify-between items-center p-4 border-b border-border">
                    <div className="flex flex-col text-left leading-tight">
                        <span className="text-base font-bold text-foreground">Connected Bytes</span>
                        <span className="text-xs font-semibold text-primary tracking-wider uppercase">Software Service</span>
                    </div>
                    <button
                        onClick={() => setIsMenuOpen(false)}
                        className="p-2 rounded-full hover:bg-foreground/10 transition-colors text-foreground"
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="py-2 h-full flex flex-col bg-background text-foreground">
                    {navItems.map((item) => (
                        <div
                            key={item.key}
                            onClick={() => handleNavClick(item.key)}
                            className={`px-6 py-4 text-base font-medium cursor-pointer transition-colors duration-200 ${location.pathname === item.key
                                ? 'text-primary bg-primary/5 border-l-2 border-primary'
                                : 'text-foreground/80 hover:bg-foreground/5 hover:text-foreground border-l-2 border-transparent'
                                }`}
                        >
                            {item.label}
                        </div>
                    ))}
                    {/* Contact Button - Now properly positioned inside the scrollable area */}
                    <div className="p-4 mt-4">
                        <button
                            onClick={handleContactClick}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground font-medium shadow-md"
                        >
                            Contact Us <GoArrowUpRight />
                        </button>
                    </div>
                </div>
            </Drawer>
        </AntHeader>
    );
};

export default Header;